import { Button, DatePicker, Divider, Dropdown, Input, Select } from 'antd';
import React, { RefObject, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import moment, { Moment } from 'moment';
import { CloseCircleFilled } from '@ant-design/icons';
import { EntityType, FilterOperator, Maybe } from '../../../../../types.generated';
import { ReactComponent as KeyboardArrowDown } from '../../../../../images/keyboard_arrow_down.svg';
import { useTranslation } from '../../../../../i18n/useTranslation';

const ApplyButton = styled(Button)`
    justify-content: center;
`;

const InnerWrapper = styled.div`
    display: flex;
    padding: 4px 8px;
`;

export enum FilterDropdownTypes {
    INPUT = 'input',
    TIME = 'time',
    DROP_DOWN = 'dropdown',
    CUSTOM = 'CUSTOM',
    NUMBER = 'number',
}

export interface IFilterDropdown {
    operators: Array<{ label: string; value: string | number }>;
    id: string;
    name: string;
    disabled?: boolean;
    entityType?: EntityType;
    dropDownOptions?: Array<{ label: string; value: string | number }>;
    placeholder?: string;
    type?: FilterDropdownTypes;
    onResetFilter?: (key: string) => void;
    onApplyFilter?: (key: string, value: string | number, condition?: Maybe<FilterOperator>, negated?: boolean) => void;
    CustomRender?: React.FC<any>;
}
const FilterDropdown = (props: IFilterDropdown) => {
    const { t } = useTranslation();
    const {
        id,
        name,
        operators,
        entityType,
        dropDownOptions = [],
        type,
        onApplyFilter = () => {},
        onResetFilter = () => {},
        CustomRender,
        disabled,
        placeholder,
    } = props;

    const [fieldValue, setFieldValue] = useState<string | number | null | Moment | boolean>(null);
    const [isApplied, setIsApplied] = useState<boolean>(false);
    const [operator, setOperator] = useState<Maybe<FilterOperator>>(operators[0].value as FilterOperator);
    const dropdownRef: RefObject<HTMLDivElement> = useRef(null);
    const filterFieldRef: any = useRef(null); // Ref for the filter field
    const buttonRef: any = useRef(null); // Ref for the apply button

    const RenderComponent = CustomRender as React.FC<any>;

    const onChangeTextHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFieldValue(event.target.value);
    };

    const onSelectHandler = (text: string) => {
        setFieldValue(text);
    };

    const onChangeDateHandler = (date: Moment | null) => {
        setFieldValue(!date ? null : moment(date).valueOf().toString());
    };

    const applyFilter = () => {
        onApplyFilter(id, fieldValue as string, operator);
        setIsApplied(true);
        dropdownRef.current?.click();
    };

    const resetFilter = (event: React.MouseEvent<HTMLInputElement>) => {
        event.stopPropagation();
        onResetFilter(id);
        setIsApplied(false);
        setFieldValue(null);
    };

    // This is a custom keydown handler to handle accessibility
    const handleFieldRef = (event) => {
        if (event.code === 'Tab') {
            event.preventDefault(); // Prevent default tabbing behavior
            event.stopPropagation(); // Stop event from bubbling and closing the dropdown

            if (event.target?.closest('.f-filter-dropdown-field')) {
                buttonRef.current?.focus(); // Use optional chaining to avoid errors
            } else {
                filterFieldRef.current?.focus(); // Use optional chaining
            }
        }
    };

    // This is a custom keydown handler to handle accessibility
    const handleCloseButton = (event) => {
        if (event.key === 'Enter') {
            resetFilter(event);
        }
    };

    const RenderInput = useMemo(() => {
        switch (type) {
            case FilterDropdownTypes.DROP_DOWN:
                return (
                    <Select
                        style={{ width: '100%' }}
                        ref={filterFieldRef}
                        className="f-filter-dropdown-field"
                        placeholder={t('Select the value')}
                        aria-label={t('Select the value')}
                        onKeyDown={handleFieldRef}
                        getPopupContainer={(triggerNode) => triggerNode.parentNode}
                        value={fieldValue as string | number}
                        onChange={setFieldValue}
                    >
                        {dropDownOptions.map(({ label, value }) => (
                            <Select.Option value={value} tabIndex={0}>
                                {label}
                            </Select.Option>
                        ))}
                    </Select>
                );
            case FilterDropdownTypes.TIME:
                return (
                    <DatePicker
                        style={{ width: '100%' }}
                        value={fieldValue ? moment.unix((fieldValue as number) / 1000) : null}
                        showTime
                        ref={filterFieldRef}
                        className="f-filter-dropdown-field"
                        onChange={onChangeDateHandler}
                        onKeyDown={handleFieldRef}
                        format="MMM DD, YYYY h:mm a"
                    />
                );
            case FilterDropdownTypes.INPUT:
                return (
                    <Input
                        ref={filterFieldRef}
                        className="f-filter-dropdown-field"
                        placeholder={t('Enter keyword or phrase')}
                        value={fieldValue as string}
                        onChange={onChangeTextHandler}
                        onKeyDown={handleFieldRef}
                    />
                );
            case FilterDropdownTypes.NUMBER:
                return (
                    <Input
                        ref={filterFieldRef}
                        type="number"
                        min={0}
                        className="f-filter-dropdown-field"
                        placeholder={t('Enter a number')}
                        value={fieldValue as number}
                        onChange={onChangeTextHandler}
                        onKeyDown={handleFieldRef}
                    />
                );
            default:
                return (
                    <RenderComponent
                        value={fieldValue}
                        type={type}
                        placeholder={placeholder ?? t('Select the User')}
                        entityType={entityType}
                        onChange={onSelectHandler}
                        handleFieldRef={handleFieldRef}
                        filterFieldRef={filterFieldRef}
                    />
                );
        }
    }, [dropDownOptions, fieldValue, type, RenderComponent, entityType, placeholder, t]);

    return (
        <Dropdown
            trigger={['click']}
            disabled={disabled}
            className={`f-filter-dropdown ${isApplied && 'f-filter-applied'}`}
            dropdownRender={() => (
                <div className="f-filter-dropdown-menu">
                    <div className="pa-2">
                        <Select
                            style={{ width: '100%' }}
                            placeholder={t('Select the operator')}
                            aria-label={t('Select the operator')}
                            getPopupContainer={(triggerNode) => triggerNode.parentNode}
                            onKeyDown={handleFieldRef} // Custom key handling
                            value={operator}
                            tabIndex={0}
                            onChange={setOperator}
                        >
                            {operators?.map(({ label, value }) => (
                                <Select.Option key={value} value={value} tabIndex={0}>
                                    {label}
                                </Select.Option>
                            ))}
                        </Select>
                    </div>
                    <Divider style={{ margin: 0 }} />
                    <div className="pa-2" style={{ width: '100%' }}>
                        {RenderInput}
                    </div>
                    <div className="pa-2">
                        <ApplyButton
                            block
                            ref={buttonRef}
                            type="primary"
                            onClick={applyFilter}
                            disabled={!operator || !fieldValue}
                            tabIndex={0}
                        >
                            {t('Apply')}
                        </ApplyButton>
                    </div>
                </div>
            )}
        >
            <Button ref={dropdownRef} type="link" tabIndex={0} aria-label={name}>
                <InnerWrapper>
                    <span>{t(name)}</span>
                    <KeyboardArrowDown className="pl-2" style={{ width: 24 }} />
                    {isApplied && (
                        <CloseCircleFilled
                            tabIndex={0}
                            className="pl-1"
                            onKeyDown={handleCloseButton}
                            onClick={resetFilter}
                        />
                    )}
                </InnerWrapper>
            </Button>
        </Dropdown>
    );
};

export default FilterDropdown;
